import { Injectable } from "@angular/core";
import { HttpHeaders } from "@angular/common/http";
import { Router } from "@angular/router";
import { Observable, BehaviorSubject, interval, timer } from "rxjs";
import { OAuthService } from "@royalty/oauth2";
import { UserInfo, LocalStorageKey } from "@royalty/model";
import { NgxPermissionsService } from "ngx-permissions";
import { PosFeatureService } from "@royalty/service";
import { PosHelper } from "@royalty/helpers";
import { CoreToolbox } from 'src/app/core/utils/core-toolbox';

/**
 * ROPC Authentication service.
 */
@Injectable()
export class AuthenticationService {

	/**
	 * Stores the URL so we can redirect after signing in.
	 */
	public redirectUrl: string;

	/**
	 * Behavior subjects of the user's status & data.
	 */
	private signinStatus = new BehaviorSubject<boolean>(false);
	private user = new BehaviorSubject<UserInfo>(new UserInfo());

	/**
	 * Scheduling of the refresh token.
	 */
	private refreshSubscription: any;

	/**
	 * Offset for the scheduling to avoid the inconsistency of data on the client.
	 */
	private offsetSeconds = 30;

	public isLoggedIn: boolean = false;

	constructor(
		private router: Router,
		private oAuthService: OAuthService,
		private permissionsService: NgxPermissionsService,
		private posFeatureService: PosFeatureService
	) { }

	public init(): void {
		// Tells all the subscribers about the new status & data.
		this.signinStatus.next(true);
		this.isLoggedIn = true;
		this.user.next(this.getUser());
		console.log(this.getUserPermissions());
		this.permissionsService.loadPermissions(this.getUserPermissions());
	}

	public storeUserInfo(userInfo: UserInfo) {
		this.oAuthService.storeUserInfo(userInfo);
	}

	public getUser(): UserInfo {
		return this.oAuthService.getUserInfo();
	}

	public signout(): void {
		// Call unlock table
		this.releaseTableOpening();
		this.oAuthService.logOut();

		this.redirectUrl = null;

		this.isLoggedIn = false;

		// Tells all the subscribers about the new status & data.
		this.signinStatus.next(false);
		this.user.next(new UserInfo());

		this.router.navigate(["/"]);

		// Unschedules the refresh token.
		this.unscheduleRefresh();
	}

	private releaseTableOpening() {
		if (this.isValidToken()) {
			const localIds = this.oAuthService.getTableIdsOpening();
			if (PosHelper.isArrayWithLength(localIds)) {
				this.posFeatureService.unLockTable(localIds, () => {
					this.oAuthService.removeTableIdsOpening();
				});
			}
		}
	}

	public isValidToken(): boolean {
		return this.oAuthService.hasValidAccessToken();
	}

	public existingToken(): boolean {
		return this.oAuthService.existingToken();
	}

	public getAuthorizationHeader(): HttpHeaders {
		// Creates header for the auth requests.
		let headers: HttpHeaders = new HttpHeaders().set(
			"Content-Type",
			"application/json"
		);
		headers = headers.append("Accept", "application/json");

		const token: string = this.oAuthService.getAccessToken();
		if (token !== "") {
			const tokenValue: string = "Bearer " + token;
			headers = headers.append("Authorization", tokenValue);
		}
		return headers;
	}

	public isSignedIn(): Observable<boolean> {
		return this.signinStatus.asObservable();
	}

	public userChanged(): Observable<UserInfo> {
		return this.user.asObservable();
	}

	public hasPermissions(hasPermissions: string[]): boolean {
		const permissions = this.getUserPermissions();
		const needPermissions: string[] =
			hasPermissions && typeof hasPermissions !== "undefined"
				? hasPermissions
				: [];
		return permissions.some(r => needPermissions.indexOf(r) >= 0);
	}

	public getUserPermissions(): string[] {
		const perms = this.oAuthService.getPermissions();
		if (perms) {
			return perms.split(",");
		}
		return [];
	}

	/**
	 * Strategy for refresh token through a scheduler.
	 * Will schedule a refresh at the appropriate time.
	 */
	public scheduleRefresh(): void {
		const source: Observable<number> = interval(
			this.calcDelay(this.getAuthTime())
		);

		this.refreshSubscription = source.subscribe(() => {
			this.oAuthService
				.refreshToken()
				.then(() => {
					// Scheduler works.
					console.log("then scheduleRefresh");
				})
				.catch((error: any) => {
					console.log("then scheduleRefresh");
					this.handleRefreshTokenError();
				});
		});
	}

	/**
	 * Case when the user comes back to the app after closing it.
	 */
	public startupTokenRefresh(): void {
		if (this.oAuthService.hasValidAccessToken()) {
			const source: Observable<number> = timer(
				this.calcDelay(new Date().valueOf())
			);
			console.log("startupTokenRefresh");
			// Once the delay time from above is reached, gets a new access token and schedules additional refreshes.
			source.subscribe(() => {
				this.oAuthService
					.refreshToken()
					.then(() => {
						console.log("then startupTokenRefresh");
						this.scheduleRefresh();
					})
					.catch((error: any) => {
						console.log("catch startupTokenRefresh");
						this.handleRefreshTokenError();
					});
			});
		}
	}

	/**
	 * Unsubscribes from the scheduling of the refresh token.
	 */
	private unscheduleRefresh(): void {
		if (this.refreshSubscription) {
			this.refreshSubscription.unsubscribe();
		}
	}

	/**
	 * Handles errors on refresh token, like expiration.
	 */
	private handleRefreshTokenError(): void {
		this.redirectUrl = this.router.url;

		// Tells all the subscribers about the new status & data.
		this.signinStatus.next(false);
		this.user.next(new UserInfo());

		// Unschedules the refresh token.
		this.unscheduleRefresh();

		// The user is forced to sign in again.
		this.router.navigate(["/"]);
	}

	private calcDelay(time: number): number {
		const expiresAt: number = this.oAuthService.getAccessTokenExpiration();
		const delay: number = expiresAt - time - this.offsetSeconds * 1000;
		return delay > 0 ? delay : 0;
	}

	private getAuthTime(): number {
		return parseInt(
			CoreToolbox.storageService.getItem(LocalStorageKey.access_token_stored_at),
			10
		);
	}
}
